import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaBullhorn,
  FaPlus,
  FaTimes,
  FaCalendarAlt,
  FaPaperPlane,
  FaSpinner
} from "react-icons/fa";
import API_BASE_URL from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";

function AnnouncementsContainer() {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    message: ""
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/projects/announcements/general`,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`
          }
        }
      );
      setAnnouncements(response.data);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast.error("Failed to fetch announcements");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.message.trim()) {
      toast.warning("Please fill in all fields");
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(
        `${API_BASE_URL}/api/projects/announcements/general`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`
          }
        }
      );

      toast.success("Announcement broadcasted successfully!");
      setShowModal(false);
      setFormData({ title: "", message: "" });
      fetchAnnouncements();
    } catch (error) {
      console.error("Error creating announcement:", error);
      toast.error(error.response?.data?.message || "Failed to create announcement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen p-6 md:p-12 font-sans bg-gray-50/30">
      <div className="max-w-6xl mx-auto">
        <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              Announcements
            </h1>
            <p className="text-gray-500 mt-2">Broadcast updates to all students and faculty</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="bg-[#B7202E] text-white px-6 py-3 rounded-xl font-bold shadow-sm hover:bg-[#961925] hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center gap-2 w-full md:w-auto justify-center"
          >
            <FaPlus /> New Announcement
          </button>
        </header>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-medium">Loading updates...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {announcements.length > 0 ? (
              announcements.map((announcement, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all group relative overflow-hidden flex flex-col"
                >
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-[#B7202E] opacity-0 group-hover:opacity-100 transition-opacity"></div>

                  <div className="flex justify-between items-start mb-4">
                    <div className="p-3 bg-red-50 text-[#B7202E] rounded-lg">
                      <FaBullhorn className="text-xl" />
                    </div>
                    <span className="text-xs font-medium text-gray-400 flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-full border border-gray-100">
                      <FaCalendarAlt />
                      {new Date(announcement.date).toLocaleDateString("en-IN", {
                        day: 'numeric', month: 'numeric', year: 'numeric'
                      })}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-gray-800 mb-2 leading-tight group-hover:text-[#B7202E] transition-colors">
                    {announcement.title}
                  </h3>

                  <p className="text-gray-600 text-sm leading-relaxed flex-grow whitespace-pre-line">
                    {announcement.message}
                  </p>
                </div>
              ))
            ) : (
              <div className="col-span-full py-16 text-center bg-white rounded-xl border border-dashed border-gray-300">
                <FaBullhorn className="text-5xl text-gray-200 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-400">No Announcements Yet</h3>
                <p className="text-gray-400 mt-2">Create a new announcement to update everyone.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-scale-up">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                New Announcement
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Project Submission Deadline Extended"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-all text-gray-800 placeholder-gray-400"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Type your announcement details here..."
                  rows="5"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-all text-gray-800 placeholder-gray-400 resize-none"
                />
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-3 rounded-xl bg-[#B7202E] text-white font-semibold hover:bg-[#961925] shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {submitting ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                  {submitting ? "Sending..." : "Post Announcement"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnouncementsContainer;
